import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Text,
  useBreakpointValue,
  Flex,
  Divider,
  Breadcrumb,
  BreadcrumbItem,
  Icon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  VStack,
  Spinner
} from '@chakra-ui/react';
import {
  faHomeLg,
  faMinus,
  faPlus,
  faUserHeadset
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonText, SearchInput } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  fetchAllPageHelpContent,
  fetchAllPageHelpContentUnauthenticated
} from 'src/api/queries';
import SubmissionLoader from 'src/pages/Organization/Workspace/Submissions/SubmissionLoader';
import HelpCenterTopicDrawer from './HelpCenterTopicPage';
import { debounce } from 'lodash';
import ContactUsProvider from 'src/pages/ContactUs/ContactUsProvider';
const HelpCenterPage = ({
  isOpen,
  setIsOpen,
  isLoggedIn
}: {
  isOpen: boolean;
  setIsOpen: (arg1: boolean) => void;
  isLoggedIn: boolean;
}) => {
  const drawerSize = useBreakpointValue({ base: 'full', md: 'md' });

  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState({ pageName: '', title: '' });
  const [selectedTopic, setSelectedTopic] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIndices, setExpandedIndices] = useState<number[]>([0]);

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 1000),
    []
  );
  useEffect(
    () => () => debouncedSetSearchTerm.cancel(),
    [debouncedSetSearchTerm]
  );
  const { data: allPageHelpData, isFetching } = useQuery({
    queryKey: ['all-page-help-content', searchTerm, isLoggedIn],
    queryFn: () =>
      isLoggedIn
        ? fetchAllPageHelpContent(searchTerm)
        : fetchAllPageHelpContentUnauthenticated(searchTerm)
  });

  const visiblePages = useMemo(
    () =>
      (allPageHelpData ?? []).filter(
        pageData => (pageData?.topics?.length ?? 0) > 0
      ),
    [allPageHelpData]
  );
  const isLoadingResults = isFetching || searchInput !== searchTerm;
  const [isContactSupportOpen, setIsContactSupportOpen] = useState(false);
  return (
    <>
      <Drawer
        isOpen={isOpen && !isHelpDrawerOpen}
        placement="right"
        onClose={() => setIsOpen(false)}
        size={drawerSize}
      >
        <DrawerOverlay />
        <DrawerContent
          display="flex"
          flexDirection="column"
          sx={{ height: { base: '100dvh', md: 'auto' } }}
        >
          <DrawerCloseButton onClick={() => setIsOpen(false)} />
          <DrawerHeader></DrawerHeader>
          <DrawerBody p={0} overflowY="auto" flex="1">
            <Flex direction={'column'} gap={3}>
              {/* Top */}
              <Flex justifyContent={'space-between'}>
                <Breadcrumb>
                  <BreadcrumbItem>
                    <Text textStyle={'headingGroup'}>
                      <Icon as={FontAwesomeIcon} icon={faHomeLg} mr="2" />
                      Help Center
                    </Text>
                  </BreadcrumbItem>
                </Breadcrumb>

                <ButtonText
                  leftIcon={<Icon as={FontAwesomeIcon} icon={faUserHeadset} />}
                  onClick={() => {
                    setIsContactSupportOpen(true);
                  }}
                  mr={3}
                >
                  Contact Support
                </ButtonText>
              </Flex>
              <Divider />
              <Flex direction={'column'} px={3}>
                <Text textStyle={'textContext'}>
                  Find quick answers for every step of the submission process.
                  Watch short walkthrough videos, browse frequently asked
                  questions by topic, or search for a specific question — all
                  without leaving your current page.
                </Text>
              </Flex>
              {/* Video Layout */}
              <Flex direction={'column'} gap={3} px={3}>
                {/* Search for topic */}
                <SearchInput
                  placeholder="Search topics"
                  value={searchInput}
                  name="search"
                  onInputClear={() => {
                    debouncedSetSearchTerm.cancel();
                    setSearchInput('');
                    setSearchTerm('');
                  }}
                  onChange={e => {
                    setSearchInput(e.target.value);
                    debouncedSetSearchTerm(e.target.value);
                  }}
                />

                {isLoadingResults ? (
                  <Flex
                    justifyContent="center"
                    alignItems="center"
                    direction="column"
                    gap={3}
                    py={8}
                  >
                    <Spinner size="lg" color="blue.500" />
                    <Text textStyle="textContext">
                      {searchInput !== searchTerm
                        ? 'Searching help content...'
                        : 'Loading help content...'}
                    </Text>
                  </Flex>
                ) : visiblePages.length > 0 ? (
                  <Accordion
                    allowMultiple
                    index={expandedIndices}
                    onChange={indices =>
                      setExpandedIndices(
                        Array.isArray(indices) ? indices : [indices]
                      )
                    }
                  >
                    <Flex gap={2} direction={'column'}>
                      {visiblePages?.map(pageData => (
                        <AccordionItem key={pageData?.id}>
                          {({ isExpanded }) => (
                            <>
                              <AccordionButton
                                px={2}
                                py={1}
                                bgColor="#F9FAFB"
                                border={'1px solid #D0D5DD'}
                                borderColor="neutralPrimaryBorder"
                              >
                                <Flex
                                  justifyContent={'space-between'}
                                  alignItems={'center'}
                                  width={'100%'}
                                >
                                  <Flex
                                    direction={'column'}
                                    alignItems={'flex-start'}
                                  >
                                    <Text textStyle="headingItem">
                                      {pageData.title}
                                    </Text>
                                    <Text textStyle="textContext">
                                      {pageData.topicCount} topics
                                    </Text>
                                  </Flex>
                                  <Flex gap={3} alignItems="center">
                                    <Box ml={2} p={2}>
                                      <FontAwesomeIcon
                                        icon={isExpanded ? faMinus : faPlus}
                                        color="blue"
                                      />
                                    </Box>
                                  </Flex>
                                </Flex>
                              </AccordionButton>
                              <AccordionPanel
                                px={1}
                                py={3}
                                border={'1px solid #D0D5DD'}
                                borderColor="neutralPrimaryBorder"
                                bgColor="primary"
                              >
                                <VStack align="stretch" spacing={1}>
                                  {pageData.topics.map(topic => (
                                    <Flex align="center" gap={2}>
                                      <ButtonText
                                        key={topic.id}
                                        justifyContent="flex-start"
                                        whiteSpace="normal"
                                        textAlign="left"
                                        fontSize="sm"
                                        onClick={() => {
                                          setSelectedTopic(topic.id);
                                          setSelectedPage({
                                            pageName: pageData.pageName,
                                            title: pageData.title
                                          });
                                          setIsHelpDrawerOpen(true);
                                        }}
                                      >
                                        {topic.title}
                                      </ButtonText>
                                    </Flex>
                                  ))}
                                </VStack>
                              </AccordionPanel>
                            </>
                          )}
                        </AccordionItem>
                      ))}
                    </Flex>
                  </Accordion>
                ) : (
                  <SubmissionLoader
                    title="No Results Found"
                    subTitle={
                      'We could not find any results matching your search criteria. Please adjust your search and try again.'
                    }
                    bgColor="neutralSecondaryFill"
                  />
                )}
              </Flex>
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      {isHelpDrawerOpen && (
        <HelpCenterTopicDrawer
          isOpen={isHelpDrawerOpen}
          setIsOpen={setIsHelpDrawerOpen}
          topicId={selectedTopic}
          pageName={selectedPage}
          isLoggedIn={isLoggedIn}
        />
      )}
      {isContactSupportOpen && (
        <ContactUsProvider
          isLoggedIn={isLoggedIn}
          isOpen={isContactSupportOpen}
          setIsOpen={setIsContactSupportOpen}
        />
      )}
    </>
  );
};

export default HelpCenterPage;
